import Popup from '@shared/components/Popup/Popup';
import cx from 'classnames';
import { ComponentType, useEffect, useState, Suspense } from 'react';

import CenteredSpinner from 'components/CenteredSpinner/CenteredSpinner';
import { IconType } from 'utils/types';

import SettingsSidebar from './SettingsSidebar/SettingsSidebar';

const modalSizes = {
  fullScreen: {
    popup: 'max-w-5xl',
    container: 'min-h-full',
  },
  md: {
    popup: 'max-w-[768px]',
    container: 'md:!h-[640px] min-h-full md:min-h-0',
  },
};

export type SettingsModalSize = keyof typeof modalSizes;

export interface Tab {
  id: number;
  title: string;
  icon: IconType;
  contentComponent: ComponentType;
  dataTestId?: string;
  subTabs?: Omit<Tab, 'subTabs'>[];
}

interface SettingsModalProps {
  title: string;
  tabs: Tab[];
  onClose: () => void;
  hasLanguageSelect?: boolean;
  size?: SettingsModalSize;
}

const SettingsModal = ({
  title,
  tabs,
  onClose,
  hasLanguageSelect = false,
  size = 'fullScreen',
}: SettingsModalProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  const allTabs = tabs.flatMap((tab) => [tab, ...(tab.subTabs ?? [])]);
  const ActiveComponent = allTabs.find((tab) => tab.id === activeTab)?.contentComponent;

  // Guard: if tabs change (e.g. feature-flag flip) and the active tab is no longer
  // present, fall back to the first available tab so the modal does not blank.
  useEffect(() => {
    if (!allTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab, allTabs]);

  if (!ActiveComponent) return null;

  return (
    <Popup
      isVisible
      closePopup={onClose}
      className={cx(
        '!my-0 !min-h-full !w-full md:!justify-center !justify-start overflow-hidden !p-4',
        modalSizes[size].popup
      )}
      wrapperId="settings-modal"
    >
      <Popup.Container className={cx('!max-w-unset !mt-0 w-full overflow-hidden', modalSizes[size].container)}>
        <div className="border-gray-20 flex items-center justify-between border-b px-4 md:px-6 py-4 font-medium text-black">
          <span className="md:text-size-xl font-medium">{title}</span>
          <Popup.CloseIcon className="relative !right-0" closePopup={onClose} />
        </div>
        {/* SettingsSidebar has order-2 on mobile (bottom footer bar) and order-1 on md+ (left sidebar) */}
        <div className="flex h-full w-full flex-col overflow-hidden md:flex-row">
          <SettingsSidebar
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            hasLanguageSelect={hasLanguageSelect}
            size={size}
          />
          <div className="bg-grayscale-gray10 order-1 h-full w-full overflow-y-auto p-4 md:order-2 md:p-6">
            <Suspense fallback={<CenteredSpinner />}>
              <ActiveComponent />
            </Suspense>
          </div>
        </div>
      </Popup.Container>
    </Popup>
  );
};

export default SettingsModal;
